import React, { useState, useEffect } from 'react';
import { complaintAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { MessageSquare, Plus } from 'lucide-react';

export default function CustomerComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ category: 'no_internet', title: '', description: '', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await complaintAPI.getAll({ limit: 20 });
      setComplaints(data.data || []);
    } catch { toast.error('Failed to load complaints'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await complaintAPI.create(form);
      toast.success('Complaint submitted! Our team will contact you soon.');
      setShowNew(false);
      setForm({ category: 'no_internet', title: '', description: '', priority: 'medium' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const STATUS_COLOR = { open: '#dc2626', in_progress: '#d97706', resolved: '#059669', closed: '#64748b' };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Support Tickets</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Raise and track your complaints</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={14} /> New Complaint
        </button>
      </div>

      {/* New complaint form */}
      {showNew && (
        <div className="card mb-6">
          <div className="card-header">
            <h3>Submit a Complaint</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Issue Type *</label>
                  <select className="form-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    <option value="no_internet">No Internet</option>
                    <option value="slow_internet">Slow Internet</option>
                    <option value="intermittent_connection">Intermittent Connection</option>
                    <option value="no_tv_signal">No TV Signal</option>
                    <option value="poor_tv_quality">Poor TV Quality</option>
                    <option value="missing_channels">Missing Channels</option>
                    <option value="billing_dispute">Billing Issue</option>
                    <option value="plan_change">Plan Change Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-control" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    <option value="low">Low — Can wait</option>
                    <option value="medium">Medium — Normal</option>
                    <option value="high">High — Urgent</option>
                    <option value="critical">Critical — No service</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Brief summary of the issue" />
              </div>
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea className="form-control" rows={4} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required
                  placeholder="Please describe the issue in detail. When did it start? What have you tried?" />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Complaint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complaint list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : complaints.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <MessageSquare size={32} />
          <h3>No complaints yet</h3>
          <p>If you're experiencing any issues, click "New Complaint" to let us know.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {complaints.map(c => (
            <div key={c._id} className="card" style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === c._id ? null : c._id)}>
              <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{c.ticketNumber}</span>
                    <span style={{ fontSize: '.72rem', padding: '2px 8px', borderRadius: 12, fontWeight: 600, background: `${STATUS_COLOR[c.status]}20`, color: STATUS_COLOR[c.status], textTransform: 'capitalize' }}>
                      {c.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '.95rem' }}>{c.title}</div>
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4, textTransform: 'capitalize' }}>
                    {c.category?.replace(/_/g, ' ')} · {format(new Date(c.createdAt), 'dd MMM yyyy')}
                  </div>
                </div>
                {c.assignedTo && (
                  <div style={{ textAlign: 'right', fontSize: '.78rem', color: 'var(--text-muted)' }}>
                    <div>Assigned to</div>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.assignedTo.name}</div>
                  </div>
                )}
              </div>
              {selected === c._id && (
                <div style={{ padding: '0 18px 14px', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <p style={{ fontSize: '.85rem', marginBottom: 12 }}>{c.description}</p>
                  {c.resolutionNotes && (
                    <div style={{ background: '#d1fae5', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem' }}>
                      <strong>Resolution:</strong> {c.resolutionNotes}
                    </div>
                  )}
                  {c.timeline?.slice(-3).reverse().map((t, i) => (
                    <div key={i} style={{ fontSize: '.78rem', color: 'var(--text-muted)', padding: '4px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none', marginTop: 4 }}>
                      <strong>{t.action}</strong> — {t.description} · {format(new Date(t.timestamp), 'dd/MM/yy HH:mm')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
